import { cn } from "../../utils";
import { Bot, Link2, MessageCircleQuestion, User } from "lucide-react";
import { FeedbackBar, type FeedbackRating } from "./FeedbackBar";
import { GuidancePrompt } from "./GuidancePrompt";
import { TypingIndicator } from "./TypingIndicator";

export type MessageRole = "user" | "assistant";

export interface MessageCitation {
	code: string;
	label: string;
	url?: string;
	type?: "score" | "doubt" | "qa" | "facility";
}

export interface Message {
	id: string;
	role: MessageRole;
	content: string;
	citations?: MessageCitation[];
	guidance_type?: string;
	suggestions?: Array<{ label: string; description?: string; query?: string }>;
	isGenerating?: boolean; // LLMがクエリ生成中かどうか
}

interface MessageBubbleProps {
	message: Message;
	onFeedbackSubmit?: (messageId: string, rating: FeedbackRating, comment: string) => void;
	onGuidanceSelect?: (selection: { label: string; query?: string }) => void;
}

export function MessageBubble({ message, onFeedbackSubmit, onGuidanceSelect }: MessageBubbleProps) {
	const isUser = message.role === "user";
	const hasGuidance = message.guidance_type && message.guidance_type !== "none" && message.suggestions && message.suggestions.length > 0;
	
	// Split citations into regular sources and Q&A
	const regularCitations = message.citations?.filter((c) => c.type !== "qa") ?? [];
	const qaCitations = message.citations?.filter((c) => c.type === "qa") ?? [];

	return (
		<div
			className={cn(
				"flex w-full gap-3 p-2",
				isUser ? "justify-end" : "justify-start",
			)}
		>
			<div
				className={cn(
					"flex max-w-[80%] flex-col gap-2",
					isUser ? "items-end" : "items-start",
				)}
			>
				<div className="flex items-center gap-2 uppercase tracking-wide text-slate-400" style={{ fontSize: '14px' }}>
					<span className="inline-flex h-9 w-9 items-center justify-center rounded-full border-2 border-slate-300 bg-slate-50" style={{ color: '#475569' }}>
						{isUser ? <User style={{ width: '22px', height: '22px', stroke: '#475569', strokeWidth: 2.5, display: 'block' }} /> : <Bot style={{ width: '22px', height: '22px', stroke: '#475569', strokeWidth: 2.5, display: 'block' }} />}
					</span>
					<span>{isUser ? "あなた" : "しろぼんAI"}</span>
				</div>
				<div
					className={cn(
						"rounded-2xl border p-4 leading-relaxed shadow-sm",
						isUser
							? "border-slate-200 bg-slate-100 text-slate-900"
							: "border-emerald-200 bg-white text-slate-900 shadow-emerald-100/70",
					)}
					style={{ fontSize: '15px' }}
				>
					{message.isGenerating ? (
						<TypingIndicator />
					) : (
						<p className="whitespace-pre-wrap">{message.content}</p>
					)}
				</div>
				{!isUser && (
					<>
						{message.citations && message.citations.length > 0 && (
							<>
								{/* 根拠リンク（score, doubt, facility） */}
								{regularCitations.length > 0 && (
							<div className="flex w-full flex-col gap-2 text-slate-500" style={{ fontSize: '14px' }}>
								<div className="font-semibold text-slate-600">根拠リンク</div>
										<div className="flex flex-wrap gap-2">
											{regularCitations.map((citation, index) => (
												<a
													key={`source-${citation.code}-${index}`}
													href={citation.url ?? "#"}
													target={citation.url ? "_blank" : undefined}
													rel={citation.url ? "noreferrer" : undefined}
													className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50/60 px-3 py-1 font-medium text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-100"
												>
													<span className="font-mono" style={{ fontSize: '12px' }}>{citation.code}</span>
													<span>{citation.label}</span>
													{citation.url && <Link2 className="h-4 w-4" />}
												</a>
											))}
										</div>
									</div>
								)}
								{/* 関連Q&A */}
								{qaCitations.length > 0 && (
							<div className="flex w-full flex-col gap-2 text-slate-500" style={{ fontSize: '14px' }}>
								<div className="font-semibold text-slate-600">関連Q&A</div>
										<div className="flex flex-wrap gap-2">
											{qaCitations.map((citation, index) => (
												<a
													key={`qa-${citation.code}-${index}`}
													href={citation.url ?? "#"}
													target={citation.url ? "_blank" : undefined}
													rel={citation.url ? "noreferrer" : undefined}
													className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50/60 px-3 py-1 font-medium text-sky-700 transition hover:border-sky-300 hover:bg-sky-100"
												>
													<MessageCircleQuestion className="h-4 w-4" />
													<span>{citation.label}</span>
													{citation.url && <Link2 className="h-4 w-4" />}
												</a>
											))}
										</div>
									</div>
								)}
								<div className="mt-2 w-full">
									<FeedbackBar
										answerKey={message.id}
										onSubmit={(rating, comment) => onFeedbackSubmit?.(message.id, rating, comment)}
									/>
								</div>
							</>
						)}
						{hasGuidance && (
							<GuidancePrompt
								type={message.guidance_type as "mode_selection" | "use_case_suggestion" | "clarify_zero_hit"}
								suggestions={message.suggestions!}
								onSelect={onGuidanceSelect!}
							/>
						)}
					</>
				)}
			</div>
		</div>
	);
}
